import * as React from "react";
import LandingCard from "./landing_card";
import { Typography, Stack, Box, ButtonBase, Button, Avatar, Divider, Skeleton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../../enums/react_query_key_enum";
import { fetchSiteActivity } from "../../../scripts/api_v2/landing_page_client";
import useBackend, { useLogin } from "../../../hooks/use_backend";
import ErrorIcon from "@mui/icons-material/Warning";
import WarningIcon from "@mui/icons-material/Error";
import { AlertColors } from "../../../components/alerting/alert_enums";
import { timeAgo } from "../../../scripts/utils";
import AlertIcon from "../../../components/alerting/alert_icon";
import { useAmplitudeTrack, useOpenSite, SITE_SELECTION_SOURCE } from "../../../scripts/amplitude_utils";
import { TOOLTIPS_EN } from "../../../enums/tooltip_enum_EN";

function SiteActivityCard({ value, data, ...props }) {
  const login = useLogin();
  const backend = useBackend();

  const [count, setCount] = React.useState(5);
  const { trackButtonClicked } = useAmplitudeTrack();

  let { data: siteActivity, isFetching: fetchingSiteActivityData } = useQuery({
    queryKey: [REACT_QUERY_KEYS.LANDING_PAGE_SITE_ACTIVITY, backend.fetch, login.company_name, count],
    queryFn: () => fetchSiteActivity(backend.fetch, count),
    refetchInterval: 60000,
    placeholderData: (previousData, previousQuery) => previousData,
  });

  const list = siteActivity?.list;
  const total = siteActivity?.total;

  React.useEffect(() => {
    total && setCount(Math.min(total, count)); // count cannot be larger than data
  }, [total]);

  return (
    <LandingCard
      tooltip={TOOLTIPS_EN.SITE_ACTIVITY}
      title="Site Activity"
      {...props}
      sx={{
        display: "flex",
        flexDirection: "column",
        ...props.sx,
      }}
    >
      <Stack spacing={1}>
        {[...Array(count)].map((_, index) => {
          const isLoaded = list && index < list.length;

          if (isLoaded) {
            return (
              <AlertItem
                key={`${list[index].site_id}-${list[index].info}-${list[index].valid_from}-${index}`}
                siteId={list[index].site_id}
                siteName={list[index].site_name}
                description={list[index].info}
                timestamp={list[index].valid_from}
                icon={<AlertIcon level={list[index].level} icon={list[index].icon} style={{ fontSize: 30 }} />}
                isLoading={false}
              />
            );
          }
          return <AlertItem key={index} isLoading={true} isHidden={!fetchingSiteActivityData} />;
        })}
      </Stack>

      {list?.length > 0 ? (
        <Button sx={{ marginTop: 1 }} onClick={() => {
          trackButtonClicked('site_activity_view_more', 'landing_page', { shown_count: count + 5 });
          setCount(count + 5);
        }}>
          View more
        </Button>
      ) : (
        <Button sx={{ marginTop: 1 }} disabled={true}>
          None found
        </Button>
      )}
    </LandingCard>
  );
}

export default SiteActivityCard;

function AlertItem({ icon, timestamp, siteId, siteName, description, isLoading, isHidden = false }) {
  const openSite = useOpenSite();

  return !isHidden ? (
    <>
      <ButtonBase
        disabled={isLoading}
        onClick={() => openSite(siteId, SITE_SELECTION_SOURCE.SITE_ACTIVITY_CARD)}
        sx={{
          width: "100%",
          alignItems: "center",
          display: "flex",
          textAlign: "left",
          borderRadius: "10px",
          px: 1,
          py: 0.5,
          "&:hover": { backgroundColor: "#f3f3f3" },
        }}
      >
        <Box
          sx={{
            minWidth: "30px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            alignSelf: "flex-start",
            marginTop: "5px",
          }}
        >
          {isLoading ? <Skeleton variant="circular" width={30} height={30} /> : icon}
        </Box>

        <Stack sx={{ flexGrow: 1, marginLeft: 2 }}>
          <Typography variant="body2">{isLoading ? <Skeleton width="80%" /> : siteName}</Typography>
          <Typography variant="caption">{isLoading ? <Skeleton width="60%" /> : description}</Typography>
          <Typography variant="caption" color={"#aaa"} sx={{ textAlign: "left" }}>
            {isLoading ? <Skeleton width="30%" /> : timeAgo(new Date(1000 * timestamp))}
          </Typography>
        </Stack>
      </ButtonBase>
    </>
  ) : null;
}
